// Data layer for the Pseudo Manager page — talks to the real PseudoManager backend API.
// Pseudo Manager candidates come from the Manager's own real direct reports (see PseudoManagers.tsx),
// not a directory search — a Manager can only elect one of their own reportees.
import { envConfig } from '../config/authConfig';
import {
  AssignPseudoManagerInput,
  PseudoManager,
  PseudoManagerReportee,
} from '../types/pseudoManager';

const BASE = envConfig.apiBaseUrl;

type ApiCall = (url: string, options?: RequestInit) => Promise<Response>;

async function unwrap<T>(res: Response): Promise<T> {
  const json = await res.json().catch(() => null);
  if (!res.ok || !json?.success) {
    throw new Error(json?.message ?? json?.errors?.[0] ?? 'Request failed');
  }
  return json.data as T;
}

/** Fetches the Manager's active Pseudo Manager assignments. */
export async function getPseudoManagers(managerId: string, apiCall: ApiCall): Promise<PseudoManager[]> {
  const res = await apiCall(`${BASE}/api/PseudoManager/manager/${encodeURIComponent(managerId)}`);
  return unwrap<PseudoManager[]>(res);
}

/** Assigns a new Pseudo Manager for the given candidate + reportees. */
export async function assignPseudoManager(input: AssignPseudoManagerInput, apiCall: ApiCall): Promise<PseudoManager> {
  const res = await apiCall(`${BASE}/api/PseudoManager/assign`, {
    method: 'POST',
    body: JSON.stringify({
      managerEmployeeId: input.managerId,
      managerDisplayName: input.managerDisplayName,
      managerEmail: input.managerEmail,
      candidate: input.candidate,
      reportees: input.reportees,
    }),
  });
  return unwrap<PseudoManager>(res);
}

/** Replaces the reportee list for an existing Pseudo Manager assignment. */
export async function updateAssignedReportees(
  pseudoManagerId: number,
  reportees: PseudoManagerReportee[],
  modifiedBy: string,
  apiCall: ApiCall
): Promise<PseudoManager> {
  const res = await apiCall(
    `${BASE}/api/PseudoManager/${pseudoManagerId}/reportees?modifiedBy=${encodeURIComponent(modifiedBy)}`,
    { method: 'PUT', body: JSON.stringify({ reportees }) }
  );
  return unwrap<PseudoManager>(res);
}

/** Removes (soft-deletes) a Pseudo Manager assignment. */
export async function removePseudoManager(
  pseudoManagerId: number,
  modifiedBy: string,
  apiCall: ApiCall
): Promise<boolean> {
  const res = await apiCall(
    `${BASE}/api/PseudoManager/${pseudoManagerId}?modifiedBy=${encodeURIComponent(modifiedBy)}`,
    { method: 'DELETE' }
  );
  return unwrap<boolean>(res);
}

/** Reportees not currently managed by any Pseudo Manager, and not themselves already a Pseudo Manager. */
export function getUnassignedReportees(
  reporteePool: PseudoManagerReportee[],
  pseudoManagers: PseudoManager[]
): PseudoManagerReportee[] {
  const assignedIds = new Set(
    pseudoManagers.flatMap((pm) => pm.assignedReportees.map((r) => r.employeeId))
  );
  const pseudoManagerIds = new Set(pseudoManagers.map((pm) => pm.employeeId));
  return reporteePool.filter((r) => !assignedIds.has(r.employeeId) && !pseudoManagerIds.has(r.employeeId));
}

/** Unassigned reportees plus (if editing) the given Pseudo Manager's own current reportees. */
export function getSelectablePoolForEdit(
  reporteePool: PseudoManagerReportee[],
  pseudoManagers: PseudoManager[],
  editingPseudoManagerId?: number
): PseudoManagerReportee[] {
  const unassigned = getUnassignedReportees(reporteePool, pseudoManagers);
  if (editingPseudoManagerId == null) return unassigned;
  const editing = pseudoManagers.find((pm) => pm.id === editingPseudoManagerId);
  if (!editing) return unassigned;
  const byId = new Map(unassigned.map((r) => [r.employeeId, r]));
  editing.assignedReportees.forEach((r) => byId.set(r.employeeId, r));
  return Array.from(byId.values());
}
